
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { getAuthUser, getMemberList } from "../store";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { Observable, of } from "rxjs";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { Subject, BehaviorSubject } from "rxjs";
import { CommonUtil } from "../../common/utils/common.utils";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntil, distinctUntilChanged, debounceTime } from "rxjs/operators";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";

import { TaskRepositoryOpsLayer } from "./task.repository-ops.layer";

@Injectable({
  providedIn: "root"
})
export class TaskRepository extends TaskRepositoryOpsLayer implements OnDestroy {
  constructor() {
    super();

    // The flow bodies live in TaskFlowService (STR-07); engine flows they chain into
    // (stats, DB pulls) resolve through this registration.
    this.flowService.registerChain(this);

    this.messageTranslatorService.translatedMessagesd$.subscribe( res => {
      this.msgs = res;
    });

    document.addEventListener("pause", () => {
      window.appInBackground = true;
      StorageService.setLocalItem("lastTimeInBackground", new Date().getTime().toString());
    });

    document.addEventListener("resume", () => {
      window.appInBackground = false;

      if (!environment.isCordova) {
        this.shouldPullOn = true;

        if (!this.isPollingRemindersStarted) {
          this.startPollingReminders();
        }

        if (!this.isPollingUpdateStarted) {
          this.startPollingTaskUpdate();
        }

        if (!this.isPollingDeleteStarted) {
          this.startPollingTaskDelete();
        }
      }
    });

    this.store.select(getOnlineStatus).pipe(takeUntil(this.isAlive$)).subscribe((isOnline) => {
      // console.log('[task.repository] isOnline: ', isOnline);
      this.isOnline = isOnline;
    });

    this.store.select(getLoginStatus).pipe(distinctUntilChanged(), takeUntil(this.isAlive$)).subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
    });

    this.store.select(getMemberList).pipe(takeUntil(this.isAlive$)).subscribe(members => {
      if (members && members.length > 0) {
        this.memberList = members;
      }
    });

    this.store.select(getAuthUser).pipe(takeUntil(this.isAlive$)).subscribe(user => {
      if (user) {
        this.authUser = user;
      }
    });

    if ((window.location !== window.parent.location) && !this.isCordovaOrElectron) {
      this.cableService.getOnMessage().pipe(takeUntil(this.isAlive$)).subscribe(cableMessage => {
        console.log("handling cable message: ", cableMessage);
        window.parent.postMessage({
          type: "VNCTASK_UPDATE"
        }, "*");
      });
    } else {
      if (!CommonUtil.isOnNativeMobileDevice()) {
        this.cableService.getOnMessage().pipe(debounceTime(300), takeUntil(this.isAlive$)).subscribe(cableMessage => {
          this.reloadApp();
        });
      }
    }


  }

  ngOnDestroy(): void {
    // Cancels the reminder/update/delete poll re-arms; they are self-rescheduling
    // and would otherwise keep firing after the repository is gone.
    this.clearPollingTimers();
    this.isAlive$.next(false);
    this.isAlive$.unsubscribe();
  }

}



export { SearchItem } from "./task.repository-state";
